import { useEffect } from "react";
import dayjs from "dayjs";
import { ValidatorResponse } from "./common/validator";

export const getLettersText = (text: any) => {
  let upperCase = text?.toUpperCase();
  let getMultipleNames = upperCase?.split(" ").filter((value: any) => value);
  return getMultipleNames?.length > 1 ? `${getMultipleNames[0][0]}${getMultipleNames[1][0]}` : upperCase?.slice(0, 2);
};

export const getDivisions = (userInfo: any) => {
  if (userInfo) {
    return Array.isArray(userInfo.userdivision) ? userInfo.userdivision : [userInfo.userdivision];
  } else {
    return [];
  }
};

/* Format date field to MM-DD-YYYY */
export const formatDate = (value: string | Date) => {
  if (value) {
    return dayjs(value).format("MM-DD-YYYY");
  } 
  return "";
};

export const formatDateTime = (value: string | Date) => {
  if (value) {
    return dayjs(value).format("MM-DD-YYYY HH:mm");
  }
  return "";
};

export const formatDateTimeMonth = (value: string | Date) => {
  if (value) {
    return dayjs(value).format("MMMM D, hh:mm A");
  }
  return "";
};

/* Function to format the phone number to xxx-xxx-xxxx format for example: 954-980-2693 */
export const formatUSPhoneNumber = (value: string) => {
  const newPhoneNumber = value.toString().replace(/\D/g, "");
  const digits = newPhoneNumber;
  if (!value) return "";

  // Check if the number of digits is 10
  if (digits.length === 10) {
    // Format the phone number as XXX-XXX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Return an empty string if the number is invalid
};

/**
 * Format the Text to camelCase
 */
export const formatToCamelCase = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/** 
Create an array of time in half-hour intervals in "HH:MM aa" format in 12 hour format
*/

export const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      times.push(time);
    }
  }
  return times;
};

export const generateTimeOptionsWithSeconds = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`;
      times.push(time);
    }
  }
  return times;
};

// function to add $ to the fields
export const formatCurrency = (value: number | string | null | undefined): string => {
  if (typeof value === "string") {
    // Return an empty string if the value is empty, preventing the dollar sign from being displayed
    if (value === "") {
      return "";
    }
    // Check if the value is a string and starts with "$"
    return value.startsWith("$") ? value : "$" + value;
  }
  const numericValue = Number(value);
  if (value != null && !isNaN(numericValue)) {
    return numericValue < 0 ? `-$${Math.abs(numericValue)}` : `$${numericValue}`;
  }
  return "";
};

export const useBrowserClosureWarning = (isTryingToLeave: any) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isTryingToLeave) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isTryingToLeave]);
};

export const textLengthValidator =
  (maxLength: number, errorMessage: string) =>
  (value: string): ValidatorResponse => {
    if (value.trim().length <= maxLength) {
      return { isValid: true, errorMessage: "" };
    }
    return { isValid: false, errorMessage };
  };

export const getNHCDateTime = (NHCDate: string | undefined, NHCTime: string | undefined): Date | null => {
  if (!NHCDate && !NHCTime) return null;
  // If only date is provided, set time to 00:00:00
  if (NHCDate && !NHCTime) {
    return new Date(`${NHCDate}T00:00:00`);
  }
  // If both date and time are provided, combine them
  if (NHCDate && NHCTime) {
    const dateStr = NHCDate.replace(' ', 'T').split('T')[0];
    // NHCTime may be in "HH:mm" or "HH:mm:ss" or HHmmss format or a number, not a string
    const timeStr = getNHCTime(NHCTime.toString());
    
    return new Date(`${dateStr}T${timeStr}`);
  }
  // If only time is provided, return null (invalid)
  return null;
}

export const getNHCTime = (NHCTime: string): string => {
  if (!NHCTime) return "00:00:00";
  // Check if NHCTime is a number (e.g., 123456) and convert to string
  if (typeof NHCTime === "number" || /^\d+$/.test(NHCTime)) {
    NHCTime = NHCTime.toString();
  }
  const fields = NHCTime.split(':');
  if (fields.length === 0) return "00:00:00"; // Empty string
  if (fields.length === 1) {
    return parseNHCTime(fields[0]);
  }
  if (fields.length >= 2) {
    const [hours, minutes, seconds] = fields;
    const nHour = Number(hours);
    const nMinute = Number(minutes);
    const nSecond = seconds ? Number(seconds) : 0;
    if (
      isNaN(nHour) || isNaN(nMinute) || isNaN(nSecond) ||
      nHour < 0 || nHour > 23 ||
      nMinute < 0 || nMinute > 59 ||
      nSecond < 0 || nSecond > 59
    ) {
      return "00:00:00"; // Invalid time values
    }
    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${(seconds ? seconds : "00").padStart(2, "0")}`;
  }
  return "00:00:00"; // Fallback
}

const parseNHCTime = (NHCTime: string): string => {
  // 1–2 digits: treat as hour only (e.g. "9" or "09" → 09:00:00)
  if (NHCTime.length <= 2) {
    const hours = NHCTime.padStart(2, "0");
    const nHour = Number(hours);
    if (isNaN(nHour) || nHour < 0 || nHour > 23) return "00:00:00";
    return `${hours}:00:00`;
  }
  if (NHCTime.length <= 4) {
    NHCTime = NHCTime.padStart(4, "0");
    const hours = NHCTime.slice(0, 2);
    const minutes = NHCTime.slice(2, 4);
    if (isNaN(Number(hours)) || isNaN(Number(minutes)) || 
      Number(hours) < 0 || Number(hours) > 23 || 
      Number(minutes) < 0 || Number(minutes) > 59) {
      return "00:00:00"; // Invalid time values
    }
    return `${hours}:${minutes}:00`;
  }
  if (NHCTime.length <= 6) {
    NHCTime = NHCTime.padStart(6, "0");
  }
  const hours = NHCTime.slice(0, 2);
  const minutes = NHCTime.slice(2, 4);
  const seconds = NHCTime.slice(4, 6);
  if (isNaN(Number(hours)) || isNaN(Number(minutes)) || isNaN(Number(seconds)) || 
      Number(hours) < 0 || Number(hours) > 23 || 
      Number(minutes) < 0 || Number(minutes) > 59 || 
      Number(seconds) < 0 || Number(seconds) > 59) {
      return "00:00:00"; // Invalid time values
    }
  return `${hours}:${minutes}:${seconds}`;
};
